import classNames from 'classnames';

import { Radio, Text } from '@revelio/ui';

import { useGeographyTaxonomyTree } from '../../selection-lists/hooks/useGeographyTaxonomyTree';
import {
  getGeographyLevelChain,
  getGeographyTaxonomyLabel,
} from '../../selection-lists/utils/geographyTaxonomyLabels';
import layoutStyles from './geography-taxonomy-option.module.css';
import styles from './preference-editors.module.css';

/**
 * One geography taxonomy radio, with its level chain as the description.
 *
 * This is its own component so it can call `useTaxonomyTree` for itself — the
 * editor renders one per entry in a variable-length `taxonomyList`, and a hook
 * inside that `.map()` would break the rules of hooks. Fetching the tree here
 * (rather than only its level metadata) is deliberate: it is the same urql
 * operation the filter UI runs on selection, so choosing this option resolves
 * from cache instead of refetching.
 */
export const GeographyTaxonomyOption = ({
  taxonomyId,
  name,
  version,
  disabled,
  disabledReason,
}: {
  taxonomyId: number;
  name: string;
  version?: string;
  disabled: boolean;
  disabledReason?: string;
}) => {
  const { levels } = useGeographyTaxonomyTree({
    taxonomyId,
  });

  const levelChain = getGeographyLevelChain(levels);

  return (
    <div className={layoutStyles.taxonomyChoice}>
      <Radio
        value={String(taxonomyId)}
        disabled={disabled}
        className={styles.appearanceRadio}
      >
        {getGeographyTaxonomyLabel(name, levels)}
      </Radio>
      {levelChain.length > 0 && (
        <Text
          fontSize="x-sm"
          className={classNames(styles.description, styles.descriptionIndent)}
        >
          {levelChain}
        </Text>
      )}
      {disabled && disabledReason && (
        <Text fontSize="x-sm" className={layoutStyles.disabledReason}>
          {disabledReason}
        </Text>
      )}
    </div>
  );
};
